import { OneBroker } from "@dune/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useGetSignedGetUrlQuery } from "../../../../../../api/queries/files.queries";

const booleanEnum = z.enum(["true", "false"]);

const zFileSchema = z.object({
	name: z.string(),
	bucket: z.string(),
	key: z.string(),
	type: z.string(),
	url: z.string(),
	size: z.number().optional(),
});

export const brokerFileFormSchema = z
	.object({
		lastname: z.string().min(1, "Le nom est requis"),
		firstname: z.string().min(1, "Le prénom est requis"),
		email: z
			.string()
			.email("Format d'email invalide")
			.min(1, "L'email est requis"),
		oriasNumber: z.string().optional(),
		siren: z.string().optional(),
		denomination: z.string().optional(),
		headquartersAddress: z.string().optional(),
		phone: z.string().min(1, "Le numéro de téléphone est requis"),

		entityList: z.array(z.string()),

		attachedToANetwork: booleanEnum.optional(),
		networkName: z.string().optional(),

		attachedToAGroup: booleanEnum.optional(),
		sirenGroup: z.string().optional(),
		companyGroupName: z.string().optional(),
		_isGroupSirenValid: z.string().optional(),
		categories: z
			.array(
				z.object({
					category: z.string(),
					status: z.enum(["REGISTERED", "NOT_REGISTERED"]),
					registrationDate: z.string(),
					categoryDeletionDate: z.string().optional(),
					fundCollection: z.boolean(),
					mandants: z.string().optional(),
				}),
			)
			.optional(),

		graphicCharter: z.object({
			logo: zFileSchema.optional(),
			color: z.string().optional(),
		}),
	})
	.superRefine((data, ctx) => {
		// Validation du SIREN du groupe
		if (
			data.attachedToAGroup === "true" &&
			data.sirenGroup &&
			data.sirenGroup.trim() !== ""
		) {
			if (data._isGroupSirenValid !== "true") {
				ctx.addIssue({
					code: "custom",
					message: "Le SIREN du groupe doit être valide",
					path: ["sirenGroup"],
				});
			}
		}
	});

export type BrokerFileFormData = z.infer<typeof brokerFileFormSchema>;

export function useEditBrokerDetailsForm(broker: OneBroker) {
	const form = useForm<BrokerFileFormData>({
		defaultValues: {
			oriasNumber: "",
			siren: "",
			denomination: "",
			headquartersAddress: "",
			lastname: "",
			firstname: "",
			email: "",
			phone: "",
			entityList: [],
			attachedToANetwork: undefined,
			networkName: undefined,
			attachedToAGroup: undefined,
			sirenGroup: undefined,
			companyGroupName: undefined,
			_isGroupSirenValid: "true",
			categories: [],
			graphicCharter: {
				logo: undefined,
				color: undefined,
			},
		},
		resolver: zodResolver(brokerFileFormSchema),
	});

	const { refetch: getSignedUrl } = useGetSignedGetUrlQuery(
		broker.graphicCharter?.logo?.key || "",
	);

	useEffect(() => {
		const initForm = async () => {
			let logoData = undefined;
			if (broker.graphicCharter?.logo) {
				const { data: signedUrl } = await getSignedUrl();
				if (signedUrl) {
					logoData = {
						name: broker.graphicCharter?.logo?.name || "",
						key: broker.graphicCharter?.logo?.key || "",
						bucket: broker.graphicCharter?.logo?.bucket || "",
						type: broker.graphicCharter?.logo?.mimeType || "",
						url: signedUrl,
					};
				}
			}
			form.reset({
				oriasNumber: broker.oriasNumber?.toString() || "",
				siren: broker.siren,
				denomination: broker.socialReasons,
				headquartersAddress: `${broker.streetNumber} ${broker.streetName} ${
					broker.postalCode
				} ${broker.city}`,
				entityList: broker.entities || [],
				attachedToANetwork: broker.network?.attachedToNetwork
					? "true"
					: "false",
				networkName: broker.network?.networkName || "",
				attachedToAGroup: broker.group?.attachedToGroup ? "true" : "false",
				sirenGroup: broker.group?.sirenGroup || "",
				companyGroupName: broker.group?.companyNameGroup || "",
				_isGroupSirenValid: "true",
				categories: broker.categories || [],
				lastname: broker.manager?.lastname || "",
				firstname: broker.manager?.firstname || "",
				email: broker.manager?.email || "",
				phone: broker.manager?.phone || "",
				graphicCharter: {
					logo: logoData,
					color: broker.graphicCharter?.color || undefined,
				},
			});
		};

		initForm();
	}, [broker, form, getSignedUrl]);

	return { form };
}
